import React from 'react';
import CardSlot from '../parts/CardSlot';
import Text from 'components/_UI/Text';
import colors from 'theme/colors';
import styled, { keyframes } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import date from 'helpers/date';
import paths from 'routes/paths';
import NotResultDashBoard from 'components/commons/NotResultDashBoard';
import breakpoints from 'theme/breakpoints';
import DynamicProfileBackground from 'components/commons/DynamicProfileBackground';
import fonts from 'theme/fonts';
import { Calendar } from 'react-feather';

export default function ProfileEvents({
  nextEvents,
  nowEvents,
}) {
  const formatTime = (time) => time?.slice(0, 5);
  const slicedNextEvents = nextEvents?.slice(0, 5);
  const slicedNowEvents = nowEvents?.slice(0, 5);

  const navigateToEvent = (slug) => {
    window.location.href = `${paths.dashboard.events}?detail=${slug}`;
  };

  const renderEventCard = (event, index) => (
    <EventCard key={index} index={index} onClick={() => navigateToEvent(event?.slug)}>
      <EventImageWrapper>
        <DynamicProfileBackground
          img={event?.images[0]}
          event
          size={'80px'}
        />
      </EventImageWrapper>
      <EventInfo>
        <EventName>{event?.name}</EventName>
        <EventMeta>
          {date?.format(event?.dates[0]?.date)} &middot; {formatTime(event?.dates[0]?.start)} - {formatTime(event?.dates[0]?.end)}
        </EventMeta>
      </EventInfo>
    </EventCard>
  );

  return (
    <CardSlot marginTop={'0'}>
      <Header>
        <IconBox>
          <Calendar size={18} color={colors.neutralLight} />
        </IconBox>
        <HeaderText>
          <Title>Eventos</Title>
          <Subtitle>Programação do estabelecimento</Subtitle>
        </HeaderText>
      </Header>

      {slicedNowEvents?.length > 0 && (
        <EventSection>
          <SectionLabel>
            <PulseDot />
            Em andamento
          </SectionLabel>
          <EventsList>
            {slicedNowEvents.map(renderEventCard)}
          </EventsList>
        </EventSection>
      )}

      <EventSection>
        <EventsList>
          {slicedNextEvents?.length > 0 &&
            slicedNextEvents.map(renderEventCard)}

          {!slicedNowEvents?.length && !slicedNextEvents?.length && (
            <ContentNotResult>
              <NotResultDashBoard
                ilustration={'events'}
                textButton={'Criar evento'}
                path={`${paths.dashboard.events}?create=1`}
                text={'Notei que você ainda não possui ativos. Que tal criar seu evento e ter uma experiência incrível?'}
              />
            </ContentNotResult>
          )}
        </EventsList>
      </EventSection>
    </CardSlot>
  );
}

const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(63, 107, 198, 0.25);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.h3`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  margin: 0;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
  margin: 0;
`;

const EventSection = styled(Column)`
  gap: 8px;

  & + & {
    margin-top: 16px;
  }
`;

const SectionLabel = styled.p`
  font-size: ${fonts.sizes.small};
  font-weight: ${fonts.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.night};
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
`;

const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.success};
  flex-shrink: 0;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const EventsList = styled(Column)`
  gap: 8px;
`;

const EventCard = styled(Row)`
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  background: ${colors.neutral};
  border: 1px solid rgba(0, 0, 0, 0.03);
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  animation: ${fadeSlideUp} 0.35s ease forwards;
  animation-delay: ${({ index }) => index * 0.06}s;
  opacity: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
`;

const EventImageWrapper = styled.div`
  width: 120px;
  flex-shrink: 0;

  & > div > img,
  & > div {
    height: 72px;
    border-radius: 10px 0 0 10px;
  }
`;

const EventInfo = styled(Column)`
  padding: 8px 12px;
  justify-content: center;
  min-width: 0;
  gap: 4px;
`;

const EventName = styled.p`
  font-size: ${fonts.sizes.default};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  margin: 0;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventMeta = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
  line-height: 1.4;
`;

const ContentNotResult = styled.div`
  @media screen and (min-width: ${breakpoints.lg}) {
    & > div {
      display: flex;
      flex-direction: column;
    }
    & > div > div {
      width: 100%;
      padding: 0 5px;
    }
  }
`;
